import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { SECTION_SPLIT_COLUMN_COUNT, type SectionSplit } from '@pageboss/shared';
import { api } from '@/lib/api';
import type {
  BlockOnWire,
  PageContentOnWire,
  PageDetail,
  PageDetailResponse,
  PageMutationResponse,
  SectionOnWire,
} from '@/lib/types';

// =====================================================================
// Section-aware autosave hook (3B-3.5c) + undo/redo (3B-3.5i).
//
// State holds `page.content.sections` as the source of truth. `page.blocks`
// is a flattened derivation kept in sync per mutation for any consumer
// (e.g. legacy code path on the API response) that still wants a flat
// view. PATCH payload sends `{ content }` only.
//
// Mutations are atomic: each one updates state, recomputes the flat
// projection, primes the debounced flush, AND maintains the past/future
// history stack so Cmd/Ctrl+Z can roll back the last structural change
// (delete, add, duplicate, move, split, merge) or the last burst of
// inline text edits (debounced 1500ms — typing a paragraph is one undo).
// =====================================================================

const AUTOSAVE_DEBOUNCE_MS = 800;
const MAX_RETRIES = 3;
const HISTORY_CAP = 50;
const TEXT_EDIT_DEBOUNCE_MS = 1500;

export type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'offline';

interface AutosaveState {
  page: PageDetail;
  status: AutosaveStatus;
  savedAt: number | null;
  errorMessage: string | null;
  // Undo stack — older PageContentOnWire snapshots, oldest first.
  // Each entry represents a state the editor was in BEFORE the next
  // structural mutation or text-edit burst.
  past: PageContentOnWire[];
  // Redo stack — newer PageContentOnWire snapshots, head is the next
  // state to redo to. Cleared whenever a new (non-undo/redo) mutation
  // lands.
  future: PageContentOnWire[];
}

interface UsePageAutosaveArgs {
  siteId: string;
  pageId: string;
}

export interface UsePageAutosaveReturn {
  page: PageDetail;
  status: AutosaveStatus;
  savedAt: number | null;
  errorMessage: string | null;
  // Title is independent of sections; PATCHed in the same payload.
  setTitle: (next: string) => void;
  // SEO meta — title, description, ogImageUrl, canonicalUrl, noIndex.
  // Passing null clears the field entirely (renderer falls back to
  // page.title for the SEO title). NOT tracked in undo history.
  setSeoMeta: (next: PageDetail['seoMeta']) => void;
  // Slug rename — NOT autosaved. Operator explicit submit; throws on
  // server error so the drawer can surface taken / invalid / etc.
  commitSlug: (next: string) => Promise<void>;
  // ---- Section-level mutations ------------------------------------
  setSections: (sections: SectionOnWire[]) => void;
  insertSection: (atIndex: number, split: SectionSplit) => string;
  removeSection: (sectionId: string) => void;
  splitSection: (sectionId: string, nextSplit: SectionSplit) => void;
  mergeSection: (sectionId: string) => void;
  // ---- Column-level mutations -------------------------------------
  insertBlock: (
    sectionId: string,
    columnIndex: number,
    atIndex: number,
    block: BlockOnWire,
  ) => void;
  removeBlock: (sectionId: string, columnIndex: number, blockId: string) => void;
  updateBlockProps: (
    sectionId: string,
    columnIndex: number,
    blockId: string,
    nextProps: Record<string, unknown>,
  ) => void;
  moveBlockWithinColumn: (
    sectionId: string,
    columnIndex: number,
    fromIndex: number,
    toIndex: number,
  ) => void;
  moveBlockBetweenColumns: (
    fromSectionId: string,
    fromColumnIndex: number,
    toSectionId: string,
    toColumnIndex: number,
    blockId: string,
    toIndex?: number,
  ) => void;
  // ---- Publish + retry --------------------------------------------
  // `page.siteStatus` (site-lifecycle #19, Task 1) reflects the SITE's
  // actual status after this resolves — same field is present on every page
  // load/mutation response, so callers read `autosave.page.siteStatus`
  // rather than a separate return value.
  publish: () => Promise<void>;
  isPublishing: boolean;
  retryNow: () => void;
  // ---- Undo / redo -------------------------------------------------
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

// Generate a section ID. Uses the same scheme as the server (`ulid`)
// when available; otherwise falls back to a time-prefixed base36 ID.
function makeId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

function flattenSections(sections: SectionOnWire[]): BlockOnWire[] {
  const out: BlockOnWire[] = [];
  for (const s of sections) {
    for (const col of s.columns) {
      for (const block of col) out.push(block);
    }
  }
  return out;
}

function mapSectionById(
  sections: SectionOnWire[],
  sectionId: string,
  fn: (section: SectionOnWire) => SectionOnWire,
): SectionOnWire[] {
  return sections.map((s) => (s.id === sectionId ? fn(s) : s));
}

function mapColumn(
  section: SectionOnWire,
  columnIndex: number,
  fn: (column: BlockOnWire[]) => BlockOnWire[],
): SectionOnWire {
  return {
    ...section,
    columns: section.columns.map((col, i) => (i === columnIndex ? fn(col) : col)),
  };
}

// Cap a past array at HISTORY_CAP, dropping oldest entries.
function capPast(past: PageContentOnWire[]): PageContentOnWire[] {
  return past.length > HISTORY_CAP ? past.slice(-HISTORY_CAP) : past;
}

// =====================================================================
// DB-backed undo (3B-3f) — best-effort persistence of past[] so undo
// survives page reload. In-memory past[] is the source of truth during
// a session; this fires POSTs to /history after each structural or
// text-burst commit. Failures are swallowed: the in-memory undo keeps
// working, only the next reload loses the unpersisted steps.
// =====================================================================

type HistoryKind = 'structural' | 'text-burst';

interface HistoryEntryOnWire {
  id: string;
  kind: HistoryKind;
  content: PageContentOnWire;
  createdAt: number;
}

async function pushHistoryToServer(
  businessId: string,
  siteId: string,
  pageId: string,
  kind: HistoryKind,
  content: PageContentOnWire,
): Promise<void> {
  try {
    await api.post(locationApi(businessId, siteId, `pages/${pageId}/history`), { kind, content });
  } catch {
    // Best-effort. Don't block the editor or surface the failure —
    // worst case the next reload starts with a partial undo stack.
  }
}

async function loadHistoryFromServer(
  businessId: string,
  siteId: string,
  pageId: string,
): Promise<PageContentOnWire[]> {
  try {
    const res = await api.get<{ entries: HistoryEntryOnWire[] }>(
      locationApi(businessId, siteId, `pages/${pageId}/history`),
    );
    return res.entries.map((e) => e.content);
  } catch {
    return [];
  }
}

export function usePageAutosave(
  initial: PageDetail,
  { siteId, pageId }: UsePageAutosaveArgs,
): UsePageAutosaveReturn {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [state, setState] = useState<AutosaveState>({
    page: initial,
    status: 'idle',
    savedAt: new Date(initial.updatedAt).getTime(),
    errorMessage: null,
    past: [],
    future: [],
  });
  const [isPublishing, setIsPublishing] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<boolean>(false);
  const queuedRef = useRef<boolean>(false);
  const currentPayloadRef = useRef<{
    title?: string;
    content?: PageContentOnWire;
    seoMeta?: PageDetail['seoMeta'];
  } | null>(null);

  // ---- Text-edit history grouping ---------------------------------
  // Holds the "before" content snapshot captured at the first keystroke
  // of a text-edit burst. Reset to null each time the burst commits to
  // past (after TEXT_EDIT_DEBOUNCE_MS of inactivity) or a structural
  // mutation runs (which flushes the burst into past atomically).
  const pendingTextSnapshotRef = useRef<PageContentOnWire | null>(null);
  const textEditDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    if (inFlightRef.current) {
      queuedRef.current = true;
      return;
    }
    if (!navigator.onLine) {
      setState((s) => ({ ...s, status: 'offline' }));
      return;
    }
    if (!currentPayloadRef.current) return;

    inFlightRef.current = true;
    setState((s) => ({ ...s, status: 'saving' }));

    let attempt = 0;
    let lastError: unknown = null;
    while (attempt < MAX_RETRIES) {
      try {
        const payload = currentPayloadRef.current;
        if (!payload) break;
        const res = await api.patch<PageMutationResponse>(
          locationApi(businessId, siteId, `pages/${pageId}`),
          payload,
        );
        setState((s) => ({
          ...s,
          // Trust local for {title, content/sections, blocks} (the user
          // may have typed/dragged during the round-trip). Accept server
          // for {updatedAt, publishedAt, publicUrl, layoutTemplate}.
          page: {
            ...res.page,
            title: s.page.title,
            content: s.page.content,
            blocks: s.page.blocks,
          },
          status: 'saved',
          savedAt: Date.now(),
          errorMessage: null,
        }));
        inFlightRef.current = false;
        if (queuedRef.current) {
          queuedRef.current = false;
          scheduleFlushRef.current();
        }
        return;
      } catch (err) {
        lastError = err;
        attempt += 1;
        if (attempt >= MAX_RETRIES) break;
        const backoff = 2 ** (attempt - 1) * 1000;
        await new Promise((r) => setTimeout(r, backoff));
      }
    }

    inFlightRef.current = false;
    setState((s) => ({
      ...s,
      status: 'error',
      errorMessage: lastError instanceof Error ? lastError.message : 'Save failed',
    }));
  }, [businessId, siteId, pageId]);

  const scheduleFlush = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setState((s) => ({ ...s, status: 'dirty' }));
    debounceRef.current = setTimeout(() => {
      void flush();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [flush]);

  // Ref so flush()'s queued-flush callback can reach scheduleFlush
  // without recreating flush on every scheduleFlush change.
  const scheduleFlushRef = useRef(scheduleFlush);
  useEffect(() => {
    scheduleFlushRef.current = scheduleFlush;
  }, [scheduleFlush]);

  // ----- Hydrate past[] from DB once on mount (best-effort).
  // 3B-3f: undo history survives page reload. Entries arrive oldest-
  // first and slot in BEFORE any client-side entries that may have
  // accumulated during the round-trip, so the relative order is
  // preserved. Cap applies after merge.
  useEffect(() => {
    let cancelled = false;
    void loadHistoryFromServer(businessId, siteId, pageId).then((entries) => {
      if (cancelled || entries.length === 0) return;
      setState((s) => ({
        ...s,
        past: capPast([...entries, ...s.past]),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId, pageId]);

  // ----- Commit any pending text-edit burst into past[].
  // Called when a structural mutation runs (so the burst lands as one
  // undo step) and when undo/redo fire (so a mid-typed burst is
  // recoverable). Updates state via setState to keep canUndo reactive.
  const commitPendingTextSnapshot = useCallback(() => {
    if (textEditDebounceRef.current) {
      clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = null;
    }
    const pending = pendingTextSnapshotRef.current;
    if (!pending) return;
    pendingTextSnapshotRef.current = null;
    setState((s) => ({
      ...s,
      past: capPast([...s.past, pending]),
      future: [],
    }));
    void pushHistoryToServer(businessId, siteId, pageId, 'text-burst', pending);
  }, [businessId, siteId, pageId]);

  // ----- Apply a structural mutation: push prev content to past[],
  // clear future[], apply the mutation. Used by every mutation EXCEPT
  // text-only updateBlockProps (which uses applyTextEdit below).
  const applyStructural = useCallback(
    (mutator: (sections: SectionOnWire[]) => SectionOnWire[]) => {
      // Flush any open text-edit burst first so the structural change
      // doesn't swallow it.
      const pendingText = pendingTextSnapshotRef.current;
      if (textEditDebounceRef.current) {
        clearTimeout(textEditDebounceRef.current);
        textEditDebounceRef.current = null;
      }
      pendingTextSnapshotRef.current = null;

      let prevContentForHistory: PageContentOnWire | null = null;
      setState((s) => {
        const prevContent = s.page.content;
        prevContentForHistory = prevContent;
        const nextSections = mutator(prevContent.sections);
        const content: PageContentOnWire = { ...prevContent, sections: nextSections };
        const blocks = flattenSections(nextSections);
        currentPayloadRef.current = {
          ...currentPayloadRef.current,
          content,
        };

        let newPast = s.past;
        if (pendingText) newPast = [...newPast, pendingText];
        newPast = capPast([...newPast, prevContent]);

        return {
          ...s,
          page: { ...s.page, content, blocks },
          past: newPast,
          future: [],
        };
      });

      // Persist BOTH the flushed text burst (if any) and the structural
      // snapshot in the same order they landed in past[].
      if (pendingText) {
        void pushHistoryToServer(businessId, siteId, pageId, 'text-burst', pendingText);
      }
      if (prevContentForHistory) {
        void pushHistoryToServer(businessId, siteId, pageId, 'structural', prevContentForHistory);
      }

      scheduleFlush();
    },
    [scheduleFlush, siteId, pageId],
  );

  // ----- Apply a text-edit mutation: capture FIRST prev in the burst
  // into pendingTextSnapshotRef, reset the 1500ms debounce timer, apply
  // the mutation. Burst commits to past[] after TEXT_EDIT_DEBOUNCE_MS
  // of inactivity OR when a structural mutation / undo / redo fires.
  const applyTextEdit = useCallback(
    (mutator: (sections: SectionOnWire[]) => SectionOnWire[]) => {
      setState((s) => {
        const prevContent = s.page.content;
        const nextSections = mutator(prevContent.sections);
        const content: PageContentOnWire = { ...prevContent, sections: nextSections };
        const blocks = flattenSections(nextSections);
        currentPayloadRef.current = {
          ...currentPayloadRef.current,
          content,
        };

        // Capture the FIRST prev in the burst — successive keystrokes
        // share the same "before" state so one undo jumps to before the
        // whole burst.
        if (!pendingTextSnapshotRef.current) {
          pendingTextSnapshotRef.current = prevContent;
        }

        return {
          ...s,
          page: { ...s.page, content, blocks },
        };
      });

      // Reset debounce timer — the burst is still active.
      if (textEditDebounceRef.current) clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = setTimeout(() => {
        textEditDebounceRef.current = null;
        const pending = pendingTextSnapshotRef.current;
        if (!pending) return;
        pendingTextSnapshotRef.current = null;
        setState((s) => ({
          ...s,
          past: capPast([...s.past, pending]),
          future: [],
        }));
        void pushHistoryToServer(businessId, siteId, pageId, 'text-burst', pending);
      }, TEXT_EDIT_DEBOUNCE_MS);

      scheduleFlush();
    },
    [scheduleFlush, siteId, pageId],
  );

  // ---- Title -------------------------------------------------------
  // Title edits are NOT tracked in undo history (rare, easy to retype).

  const setTitle = useCallback(
    (next: string) => {
      setState((s) => {
        const updated = { ...s.page, title: next };
        currentPayloadRef.current = {
          ...currentPayloadRef.current,
          title: next,
        };
        return { ...s, page: updated };
      });
      scheduleFlush();
    },
    [scheduleFlush],
  );

  // SEO meta — patched on PATCH /pages/:id alongside title/content.
  // The server's seoMetaSchema is strict (title 1-60, description
  // 80-160 chars, valid URLs); the drawer surfaces those constraints
  // as helper text but lets the operator keep typing — autosave will
  // 4xx and the editor will surface the error. To clear all fields,
  // pass null.
  const setSeoMeta = useCallback(
    (next: PageDetail['seoMeta']) => {
      setState((s) => {
        currentPayloadRef.current = {
          ...currentPayloadRef.current,
          seoMeta: next,
        };
        return { ...s, page: { ...s.page, seoMeta: next } };
      });
      scheduleFlush();
    },
    [scheduleFlush],
  );

  // Slug rename — explicit submit, not autosaved. Sends a dedicated
  // PATCH with ONLY {slug} so it doesn't get bundled with in-flight
  // text/content edits. Throws on 4xx/5xx so the caller (SeoDrawer)
  // can show the error inline.
  const commitSlug = useCallback(
    async (next: string) => {
      const res = await api.patch<PageMutationResponse>(
        locationApi(businessId, siteId, `pages/${pageId}`),
        { slug: next },
      );
      setState((s) => ({
        ...s,
        page: {
          ...res.page,
          // Trust local for in-flight edits the user may have made
          // during the round-trip; accept server for slug + publicUrl
          // + updatedAt which change as a result of the rename.
          title: s.page.title,
          content: s.page.content,
          blocks: s.page.blocks,
          seoMeta: s.page.seoMeta,
        },
      }));
    },
    [siteId, pageId],
  );

  // ---- Section-level mutations ------------------------------------

  const setSections = useCallback(
    (sections: SectionOnWire[]) => {
      applyStructural(() => sections);
    },
    [applyStructural],
  );

  const insertSection = useCallback(
    (atIndex: number, split: SectionSplit): string => {
      const id = makeId();
      const columnCount = SECTION_SPLIT_COLUMN_COUNT[split];
      const newSection: SectionOnWire = {
        id,
        split,
        columns: Array.from({ length: columnCount }, () => []),
      };
      applyStructural((sections) => {
        const clamped = Math.max(0, Math.min(atIndex, sections.length));
        const next = [...sections];
        next.splice(clamped, 0, newSection);
        return next;
      });
      return id;
    },
    [applyStructural],
  );

  const removeSection = useCallback(
    (sectionId: string) => {
      applyStructural((sections) => sections.filter((s) => s.id !== sectionId));
    },
    [applyStructural],
  );

  const splitSection = useCallback(
    (sectionId: string, nextSplit: SectionSplit) => {
      applyStructural((sections) =>
        mapSectionById(sections, sectionId, (sec) => {
          if (sec.split === nextSplit) return sec;
          const columnCount = SECTION_SPLIT_COLUMN_COUNT[nextSplit];
          // All existing blocks fall into column 0; remaining columns empty.
          const allBlocks = sec.columns.flat();
          const columns: BlockOnWire[][] = Array.from({ length: columnCount }, (_, i) =>
            i === 0 ? allBlocks : [],
          );
          return { ...sec, split: nextSplit, columns };
        }),
      );
    },
    [applyStructural],
  );

  const mergeSection = useCallback(
    (sectionId: string) => {
      applyStructural((sections) =>
        mapSectionById(sections, sectionId, (sec) => {
          if (sec.split === 'full') return sec;
          const allBlocks = sec.columns.flat();
          return { ...sec, split: 'full', columns: [allBlocks] };
        }),
      );
    },
    [applyStructural],
  );

  // ---- Column-level mutations -------------------------------------

  const insertBlock = useCallback(
    (sectionId: string, columnIndex: number, atIndex: number, block: BlockOnWire) => {
      applyStructural((sections) =>
        mapSectionById(sections, sectionId, (sec) =>
          mapColumn(sec, columnIndex, (col) => {
            const clamped = Math.max(0, Math.min(atIndex, col.length));
            const next = [...col];
            next.splice(clamped, 0, block);
            return next;
          }),
        ),
      );
    },
    [applyStructural],
  );

  const removeBlock = useCallback(
    (sectionId: string, columnIndex: number, blockId: string) => {
      applyStructural((sections) =>
        mapSectionById(sections, sectionId, (sec) =>
          mapColumn(sec, columnIndex, (col) => col.filter((b) => b.id !== blockId)),
        ),
      );
    },
    [applyStructural],
  );

  // updateBlockProps is the ONLY mutation that flows through
  // applyTextEdit — most inline edits (hero headline, paragraph copy,
  // CTA label) come through here on every keystroke. Burst grouping
  // collapses N keystrokes into one undo step.
  const updateBlockProps = useCallback(
    (
      sectionId: string,
      columnIndex: number,
      blockId: string,
      nextProps: Record<string, unknown>,
    ) => {
      applyTextEdit((sections) =>
        mapSectionById(sections, sectionId, (sec) =>
          mapColumn(sec, columnIndex, (col) =>
            col.map((b) => (b.id === blockId ? { ...b, props: nextProps } : b)),
          ),
        ),
      );
    },
    [applyTextEdit],
  );

  const moveBlockWithinColumn = useCallback(
    (sectionId: string, columnIndex: number, fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      applyStructural((sections) =>
        mapSectionById(sections, sectionId, (sec) =>
          mapColumn(sec, columnIndex, (col) => {
            if (fromIndex < 0 || fromIndex >= col.length) return col;
            const clampedTo = Math.max(0, Math.min(toIndex, col.length - 1));
            const next = [...col];
            const [moved] = next.splice(fromIndex, 1);
            if (!moved) return col;
            next.splice(clampedTo, 0, moved);
            return next;
          }),
        ),
      );
    },
    [applyStructural],
  );

  const moveBlockBetweenColumns = useCallback(
    (
      fromSectionId: string,
      fromColumnIndex: number,
      toSectionId: string,
      toColumnIndex: number,
      blockId: string,
      toIndex?: number,
    ) => {
      applyStructural((sections) => {
        // First locate the moving block in its source location.
        const fromSection = sections.find((s) => s.id === fromSectionId);
        if (!fromSection) return sections;
        const fromColumn = fromSection.columns[fromColumnIndex];
        if (!fromColumn) return sections;
        const moving = fromColumn.find((b) => b.id === blockId);
        if (!moving) return sections;

        // Special-case same-section, same-column → use within-column instead.
        if (fromSectionId === toSectionId && fromColumnIndex === toColumnIndex) {
          const fromIndex = fromColumn.findIndex((b) => b.id === blockId);
          const target =
            typeof toIndex === 'number'
              ? Math.max(0, Math.min(toIndex, fromColumn.length - 1))
              : fromColumn.length - 1;
          if (fromIndex === target) return sections;
          return mapSectionById(sections, fromSectionId, (sec) =>
            mapColumn(sec, fromColumnIndex, (col) => {
              const next = [...col];
              const [m] = next.splice(fromIndex, 1);
              if (!m) return col;
              next.splice(target, 0, m);
              return next;
            }),
          );
        }

        // Two-phase: remove from source, insert into target. Both may
        // be in the same section, so we run both maps within one pass.
        return sections.map((sec) => {
          if (sec.id === fromSectionId && sec.id === toSectionId) {
            // Same section, different columns.
            return {
              ...sec,
              columns: sec.columns.map((col, i) => {
                if (i === fromColumnIndex) return col.filter((b) => b.id !== blockId);
                if (i === toColumnIndex) {
                  const next = [...col];
                  const target =
                    typeof toIndex === 'number' ? Math.max(0, Math.min(toIndex, col.length)) : col.length;
                  next.splice(target, 0, moving);
                  return next;
                }
                return col;
              }),
            };
          }
          if (sec.id === fromSectionId) {
            return mapColumn(sec, fromColumnIndex, (col) =>
              col.filter((b) => b.id !== blockId),
            );
          }
          if (sec.id === toSectionId) {
            return mapColumn(sec, toColumnIndex, (col) => {
              const next = [...col];
              const target =
                typeof toIndex === 'number' ? Math.max(0, Math.min(toIndex, col.length)) : col.length;
              next.splice(target, 0, moving);
              return next;
            });
          }
          return sec;
        });
      });
    },
    [applyStructural],
  );

  // ---- Undo / Redo -------------------------------------------------

  const undo = useCallback(() => {
    // Flush any open text-edit burst into past atomically so a typing
    // user can press Cmd+Z mid-burst and land on the pre-burst state.
    const pendingText = pendingTextSnapshotRef.current;
    if (textEditDebounceRef.current) {
      clearTimeout(textEditDebounceRef.current);
      textEditDebounceRef.current = null;
    }
    pendingTextSnapshotRef.current = null;

    setState((s) => {
      let combinedPast = s.past;
      if (pendingText) combinedPast = capPast([...combinedPast, pendingText]);
      if (combinedPast.length === 0) return s;

      const prevSnapshot = combinedPast[combinedPast.length - 1]!;
      const newPast = combinedPast.slice(0, -1);
      const newFuture = [s.page.content, ...s.future].slice(0, HISTORY_CAP);

      const blocks = flattenSections(prevSnapshot.sections);
      currentPayloadRef.current = {
        ...currentPayloadRef.current,
        content: prevSnapshot,
      };

      return {
        ...s,
        page: { ...s.page, content: prevSnapshot, blocks },
        past: newPast,
        future: newFuture,
      };
    });
    scheduleFlush();
  }, [scheduleFlush]);

  const redo = useCallback(() => {
    // Flush any open text-edit burst — operator pressed Cmd+Shift+Z;
    // we shouldn't carry stale pending state across the redo boundary.
    commitPendingTextSnapshot();

    setState((s) => {
      if (s.future.length === 0) return s;
      const nextSnapshot = s.future[0]!;
      const newFuture = s.future.slice(1);
      const newPast = capPast([...s.past, s.page.content]);

      const blocks = flattenSections(nextSnapshot.sections);
      currentPayloadRef.current = {
        ...currentPayloadRef.current,
        content: nextSnapshot,
      };

      return {
        ...s,
        page: { ...s.page, content: nextSnapshot, blocks },
        past: newPast,
        future: newFuture,
      };
    });
    scheduleFlush();
  }, [scheduleFlush, commitPendingTextSnapshot]);

  // ---- Publish -----------------------------------------------------

  const publish = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (currentPayloadRef.current) {
      await flush();
    }
    setIsPublishing(true);
    try {
      const res = await api.post<PageMutationResponse>(
        locationApi(businessId, siteId, `pages/${pageId}/publish`),
      );
      setState((s) => ({
        ...s,
        page: {
          ...res.page,
          title: s.page.title,
          content: s.page.content,
          blocks: s.page.blocks,
        },
      }));
    } finally {
      setIsPublishing(false);
    }
  }, [businessId, siteId, pageId, flush]);

  // ---- Offline / online listeners ----------------------------------

  useEffect(() => {
    function onOnline() {
      setState((s) =>
        s.status === 'offline'
          ? { ...s, status: currentPayloadRef.current ? 'dirty' : 'idle' }
          : s,
      );
      if (currentPayloadRef.current) scheduleFlush();
    }
    function onOffline() {
      setState((s) => ({ ...s, status: 'offline' }));
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [scheduleFlush]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (textEditDebounceRef.current) clearTimeout(textEditDebounceRef.current);
    };
  }, []);

  return useMemo(
    () => ({
      page: state.page,
      status: state.status,
      savedAt: state.savedAt,
      errorMessage: state.errorMessage,
      setTitle,
      setSeoMeta,
      commitSlug,
      setSections,
      insertSection,
      removeSection,
      splitSection,
      mergeSection,
      insertBlock,
      removeBlock,
      updateBlockProps,
      moveBlockWithinColumn,
      moveBlockBetweenColumns,
      publish,
      isPublishing,
      retryNow: () => void flush(),
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
    }),
    [
      state,
      setTitle,
      setSeoMeta,
      commitSlug,
      setSections,
      insertSection,
      removeSection,
      splitSection,
      mergeSection,
      insertBlock,
      removeBlock,
      updateBlockProps,
      moveBlockWithinColumn,
      moveBlockBetweenColumns,
      publish,
      isPublishing,
      flush,
      undo,
      redo,
    ],
  );
}

// Format a savedAt timestamp into a human "Saved · just now" indicator.
// Pure; caller re-renders periodically via a ticking interval.
export function formatSavedAt(savedAt: number | null, now: number = Date.now()): string {
  if (savedAt === null) return 'Not saved yet';
  const seconds = Math.floor((now - savedAt) / 1000);
  if (seconds < 5) return 'Saved · just now';
  if (seconds < 60) return `Saved · ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Saved · ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Saved · ${hours}h ago`;
  return 'Saved';
}

export async function loadPage(businessId: string, siteId: string, pageId: string): Promise<PageDetail> {
  const res = await api.get<PageDetailResponse>(locationApi(businessId, siteId, `pages/${pageId}`));
  return res.page;
}
